import React, { createContext, useContext, useEffect, useState } from 'react'
import { InboxMemberType } from '../../server/models/InboxMember'
import { getInboxesOfUser } from '../requests/inboxMembers'

interface MyInboxesContextType {
  inboxMemberships: InboxMemberType[]
  loading: boolean
  error: Error | null
  refreshInboxes: () => void
  addOrUpdateInbox: (inboxMembership: InboxMemberType) => void
  removeInbox: (inboxId: string) => void
}

const MyInboxesContext = createContext<MyInboxesContextType>({
  inboxMemberships: [],
  loading: false,
  error: null,
  refreshInboxes: () => { },
  addOrUpdateInbox: () => { },
  removeInbox: () => { }
})

export const useMyInboxes = () => useContext(MyInboxesContext)

export const MyInboxesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inboxMemberships, setInboxMemberships] = useState<InboxMemberType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchInboxes = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await getInboxesOfUser('me')
      setInboxMemberships(response.data)
    } catch (err) {
      setError(err as Error)
      console.error('Failed to fetch inboxes:', err)
    } finally {
      setLoading(false)
    }
  }

  const refreshInboxes = () => {
    fetchInboxes()
  }

  // Add or update an inbox membership
  const addOrUpdateInbox = (inboxMembership: InboxMemberType) => {
    setInboxMemberships(prevInboxes => {
      const existingIndex = prevInboxes.findIndex(
        i => i._id === inboxMembership._id || i.inbox?._id === inboxMembership.inbox?._id
      )
      
      if (existingIndex >= 0) {
        // Update existing inbox
        const updatedInboxes = [...prevInboxes]
        updatedInboxes[existingIndex] = inboxMembership
        return updatedInboxes
      } else {
        // Add new inbox
        return [...prevInboxes, inboxMembership]
      }
    })
  }

  // Remove an inbox by inboxId
  const removeInbox = (inboxId: string) => {
    setInboxMemberships(prevInboxes => 
      prevInboxes.filter(i => i.inbox?._id !== inboxId)
    )
  }

  useEffect(() => {
    fetchInboxes()
  }, [])

  return (
    <MyInboxesContext.Provider 
      value={{ 
        inboxMemberships, 
        loading, 
        error, 
        refreshInboxes,
        addOrUpdateInbox,
        removeInbox
      }}
    >
      {children}
    </MyInboxesContext.Provider>
  )
} 
